import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormatters } from '@/locale/utils/formatters'
import { useValidators } from '@/locale/utils/validators'
import { useRTL } from '@/locale/utils/rtl'
import { getCurrency } from '@/locale/index'

export const useAppI18n = () => {
  const { t, locale, te } = useI18n()
  const formatters = useFormatters()
  const validators = useValidators()

  // Detect user's locale based on browser language or location
  const detectUserLocale = async (): Promise<string> => {
    // Priority 1: Check if locale is already set in localStorage
    const savedLocale = localStorage.getItem('user-locale')
    if (savedLocale && ['en', 'ar'].includes(savedLocale)) {
      return savedLocale
    }

    // Priority 2: Check browser language
    const browserLang = navigator.language.toLowerCase()
    if (browserLang.startsWith('ar')) {
      return 'ar'
    } else if (browserLang.startsWith('en')) {
      return 'en'
    }

    // Priority 3: Fallback to geolocation-based detection
    try {
      const countryCode = await detectCountryByIP()
      // List of Arabic-speaking countries
      const arabicCountries = [
        'SA', 'AE', 'EG', 'IQ', 'JO', 'LB', 'KW', 'QA', 'BH', 'OM', 
        'SY', 'YE', 'PS', 'MR', 'SD', 'SO', 'DJ', 'KM', 'TD', 'ER'
      ]
      
      return arabicCountries.includes(countryCode) ? 'ar' : 'en'
    } catch (error) {
      console.warn('Geolocation detection failed, defaulting to English')
      return 'en' // Default fallback
    }
  }

  // Detect country by IP using a free API
  const detectCountryByIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://ipapi.co/country/')
      if (response.ok) {
        return await response.text()
      }
      throw new Error('IP detection failed')
    } catch (error) {
      // Fallback to another IP geolocation service
      try {
        const response = await fetch('https://api.country.is/')
        if (response.ok) {
          const data = await response.json()
          return data.country
        }
      } catch (fallbackError) {
        console.warn('All geolocation services failed')
        throw fallbackError
      }
      throw error
    }
  }

  // Initialize locale detection
  const initializeLocale = async () => {
    console.log('Detecting user locale...')
    const detectedLocale = await detectUserLocale()
    locale.value = detectedLocale
    // Save to localStorage for future visits
    localStorage.setItem('user-locale', detectedLocale)
    console.log(`Locale set to: ${detectedLocale}`)
  }

  // Run locale detection when composable is used
  // onMounted(() => {
  //   // initializeLocale()
  // })

  // Safe translation with fallback
  const safeTranslate = (key: string, fallback?: string) => {
    if (te(key)) {
      return t(key)
    }
    console.warn(`Translation key not found: ${key}`)
    return fallback || key
  }

  // Namespaced translations
  const common = computed(() => (key: string, params?: any) => 
    safeTranslate(`common.${key}`, key)
  )
  
  const navigation = computed(() => (key: string, params?: any) => 
    safeTranslate(`navigation.${key}`, key)
  )
  
  const social = computed(() => (key: string, params?: any) => 
    safeTranslate(`social.${key}`, key)
  )
  
  const trading = computed(() => (key: string, params?: any) => 
    safeTranslate(`trading.${key}`, key)
  )

  const localePrice = (amount: number) => {
    const handleCurrency = formatters.currency
    const currency = handleCurrency(amount, getCurrency())
    return currency
  }

  // Manual locale setter (in case user wants to override later)
  const setLocale = (newLocale: 'en' | 'ar') => {
    if (['en', 'ar'].includes(newLocale)) {
      locale.value = newLocale
      localStorage.setItem('user-locale', newLocale)
    }
  }

  return {
    // Core i18n
    t: safeTranslate,
    locale,
    setLocale,

    // Namespaced translations
    common: common.value,
    navigation: navigation.value,
    social: social.value,
    trading: trading.value,

    // Utilities
    formatters,
    validators,
    isRTL: computed(() => locale.value === 'ar'),
    getDirectionalClass: () => locale.value === 'ar' ? 'rtl' : 'ltr',
    localePrice,
  }
}